import React, { useId, useMemo, useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { alpha } from '@mui/material/styles';
import { Box, Stack, Typography } from '@mui/material';
import {
  ChartContainer,
  ChartEmptyState,
  ChartTooltip,
  InteractiveLegend,
  chartAnimation,
  formatCompactNumber,
  getSeriesColor,
  toSafeId,
  useChartTheme,
} from './chartTheme';
import { colors, typography } from '../../design-system';

const PieChartWithLegend = ({
  data = [],
  title,
  height = 320,
  showLabels = true,
  showLegend = true,
  innerRadius,
  animated = true,
  customColors = [],
}) => {
  const chartId = useId().replace(/:/g, '-');
  const chartTheme = useChartTheme(height);
  const [hiddenKeys, setHiddenKeys] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);

  const palette = customColors.length ? customColors : undefined;

  const dataWithColors = useMemo(() => {
    const visibleData = data.map((item, index) => ({
      ...item,
      fill: item.color || palette?.[index] || getSeriesColor(index),
    }));

    const total = visibleData.reduce((sum, item) => sum + Number(item.value || 0), 0);

    return visibleData.map((item) => ({
      ...item,
      total,
      percentage: total > 0 ? ((Number(item.value || 0) / total) * 100) : 0,
    }));
  }, [data, palette]);

  const visibleData = dataWithColors.filter((item) => !hiddenKeys.includes(item.name));
  const total = visibleData.reduce((sum, item) => sum + Number(item.value || 0), 0);
  const resolvedInnerRadius = innerRadius ?? (chartTheme.isMobile ? 48 : 72);
  const resolvedOuterRadius = chartTheme.isMobile ? 74 : 102;

  const toggleItem = (name) => {
    setHiddenKeys((current) => (
      current.includes(name)
        ? current.filter((key) => key !== name)
        : [...current, name]
    ));
  };

  const renderLabel = ({ cx, cy, midAngle, innerRadius: pieInnerRadius, outerRadius, percent }) => {
    if (!showLabels || chartTheme.isMobile || percent < 0.08) {
      return null;
    }

    const radius = pieInnerRadius + ((outerRadius - pieInnerRadius) * 0.5);
    const angle = (-midAngle * Math.PI) / 180;
    const x = cx + (radius * Math.cos(angle));
    const y = cy + (radius * Math.sin(angle));

    return (
      <text
        x={x}
        y={y}
        fill={colors.text.white}
        textAnchor="middle"
        dominantBaseline="central"
        style={{
          fontSize: 12,
          fontWeight: typography.fontWeight.semibold,
        }}
      >
        {`${Math.round(percent * 100)}%`}
      </text>
    );
  };

  if (!data.length) {
    return (
      <ChartContainer title={title}>
        <ChartEmptyState />
      </ChartContainer>
    );
  }

  return (
    <ChartContainer title={title}>
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <defs>
            {dataWithColors.map((item) => (
              <linearGradient key={item.name} id={`${chartId}-${toSafeId(item.name)}-pie`} x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor={alpha(item.fill, 0.98)} />
                <stop offset="100%" stopColor={alpha(item.fill, 0.62)} />
              </linearGradient>
            ))}
          </defs>

          <Pie
            data={visibleData}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={resolvedInnerRadius}
            outerRadius={resolvedOuterRadius}
            paddingAngle={3}
            cornerRadius={chartTheme.barRadius}
            labelLine={false}
            label={renderLabel}
            animationDuration={animated ? chartAnimation.duration : 0}
            animationBegin={animated ? chartAnimation.begin : 0}
            onMouseEnter={(_, index) => setActiveIndex(index)}
            onMouseLeave={() => setActiveIndex(-1)}
          >
            {visibleData.map((item, index) => (
              <Cell
                key={item.name}
                fill={`url(#${chartId}-${toSafeId(item.name)}-pie)`}
                stroke={alpha(colors.neutral.white, 0.8)}
                strokeWidth={2}
                opacity={activeIndex === -1 || activeIndex === index ? 1 : 0.6}
              />
            ))}
          </Pie>

          <text x="50%" y="46%" textAnchor="middle" fill={chartTheme.palette.textMuted} style={{ fontSize: chartTheme.isMobile ? 11 : 12 }}>
            Total
          </text>
          <text
            x="50%"
            y="54%"
            textAnchor="middle"
            fill={chartTheme.palette.text}
            style={{
              fontSize: chartTheme.isMobile ? 18 : 24,
              fontWeight: typography.fontWeight.bold,
            }}
          >
            {formatCompactNumber(total)}
          </text>

          <Tooltip
            content={(
              <ChartTooltip
                valueFormatter={(value, _name, entry) => `${formatCompactNumber(value)} · ${entry.payload.percentage.toFixed(1)}%`}
              />
            )}
          />
        </PieChart>
      </ResponsiveContainer>

      {showLegend && (
        <InteractiveLegend
          items={dataWithColors.map((item) => ({
            dataKey: item.name,
            name: `${item.name} · ${item.percentage.toFixed(0)}%`,
            color: item.fill,
          }))}
          hiddenKeys={hiddenKeys}
          onToggle={toggleItem}
          isMobile={chartTheme.isMobile}
        />
      )}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        justifyContent="center"
        sx={{ mt: 1.5 }}
      >
        {visibleData.slice(0, chartTheme.isMobile ? 2 : 3).map((item) => (
          <Box
            key={item.name}
            sx={{
              px: 1.5,
              py: 1,
              borderRadius: 999,
              backgroundColor: alpha(item.fill, 0.1),
              color: 'text.secondary',
            }}
          >
            <Typography sx={{ fontSize: typography.fontSize.xs }}>
              <strong style={{ color: chartTheme.palette.text }}>{item.name}</strong> · {formatCompactNumber(item.value)}
            </Typography>
          </Box>
        ))}
      </Stack>
    </ChartContainer>
  );
};

export default PieChartWithLegend;
